import {
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
} from '@chakra-ui/react';
import MobileRowLabel from '../mobile-row-label';
import { Pagination } from 'src/components/atoms';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { capitalize } from 'lodash';
import { MdMoreVert } from 'react-icons/md';

interface ActionsMenuItem {
  label: string;
  clickHandler: (row: any) => void;
  icon?: React.ReactElement;
  renderCondition?: (row: any) => boolean;
  isDisabled?: boolean | ((row: any) => boolean);
}

interface Column {
  label: string;
  key: string;
  width?: string;
  render?: (row: any) => React.ReactNode;
}

interface LomaTableProps {
  columns: Column[];
  identifier: string;
  data: { [key: string]: any }[];
  usePagination?: boolean;
  sortable?: boolean;
  pageNumber?: string | null;
  nextLink?: string | null;
  prevLink?: string | null;
  totalPages?: number | null;
  sortOptions?: { key: string; order: string };
  handleNextPage?: () => void;
  handlePrevPage?: () => void;
  updateSortOptions?: (sortOptions: { key: string; order: string }) => void;
  actionsMenuItems?: ActionsMenuItem[];
}

const LomaTable = ({
  columns,
  identifier,
  data,
  usePagination = false,
  sortable = false,
  pageNumber = null,
  nextLink = null,
  prevLink = null,
  totalPages = null,
  actionsMenuItems = [],
  sortOptions = { key: '', order: '' },
  handleNextPage = () => {
    console.log('Next page');
  },
  handlePrevPage = () => {
    console.log('Prev page');
  },
  updateSortOptions = () => {
    console.log('Sort by');
  },
}: LomaTableProps) => {
  return (
    <Flex direction="column" width="100%">
      <Flex
        py={4}
        w="100%"
        borderBottom="1px solid"
        borderColor="gray.200"
        gap={4}
        display={{ base: 'none', lg: 'flex' }}
        color="gray.600"
        textTransform="uppercase"
        fontSize="xs"
        fontWeight="bold"
        px={4}
      >
        {columns.map((column) => (
          <Flex
            key={column.key}
            w={{
              lg:
                actionsMenuItems.length > 0
                  ? column.width || `${Math.floor(90 / columns.length)}%`
                  : column.width || `${Math.floor(100 / columns.length)}%`,
            }}
            {...(sortable
              ? {
                  cursor: 'pointer',
                  onClick: () =>
                    updateSortOptions({
                      key: column.key,
                      order: sortOptions.order === 'asc' ? 'desc' : 'asc',
                    }),
                }
              : {})}
          >
            <Flex direction={'row'} alignItems={'center'} justifyItems={'flex-start'} gap={2}>
              {column.label}
              {sortable && sortOptions.key === column.key && (
                <ChevronDownIcon
                  boxSize={4}
                  transform={sortOptions.order === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)'}
                />
              )}
            </Flex>
          </Flex>
        ))}
        {actionsMenuItems.length > 0 && (
          <Flex w="10%" justifyContent="end">
            Actions
          </Flex>
        )}
      </Flex>
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        alignItems={'center'}
        direction={'row'}
        gap={4}
        flexWrap={'nowrap'}
        my={4}
      >
        <Text width={'70px'}>Sort by</Text>
        <Select
          name="sort_by"
          value={sortOptions.key}
          onChange={(e) => updateSortOptions({ key: e.target.value, order: sortOptions.order })}
        >
          {columns.map((column) => (
            <option key={column.key} value={column.key}>
              {capitalize(column.label)}
            </option>
          ))}
        </Select>
      </Flex>
      {data.map((item) => (
        <Flex
          key={item[identifier]}
          flexDirection={{ base: 'column', lg: 'row' }}
          w="100%"
          borderBottom="1px solid"
          borderColor="gray.200"
          py={{ base: 6, lg: 2 }}
          gap={4}
          alignItems="flex-start"
        >
          {columns.map((column, index) => (
            <Flex
              key={`${item[identifier]}-${column.key}`}
              direction="column"
              overflow={'auto'}
              w={{
                lg:
                  actionsMenuItems.length > 0
                    ? column.width || `${Math.floor(90 / columns.length)}%`
                    : column.width || `${Math.floor(100 / columns.length)}%`,
              }}
              flexGrow={0}
              {...(index === 0 ? { ml: { base: 0, lg: 4 } } : {})}
              {...(index === columns.length - 1 ? { mr: 4 } : {})}
            >
              <MobileRowLabel label={column.label} />
              {column.render ? column.render(item) : item[column.key]}
            </Flex>
          ))}
          {actionsMenuItems.length > 0 && (
            <Flex display={{ base: 'flex', lg: 'none' }} direction="row" gap={2} flexWrap={'wrap'}>
              {actionsMenuItems.map(
                (menu, index) =>
                  ((menu.renderCondition && menu.renderCondition(item)) ||
                    !menu.renderCondition) && (
                    <Button
                      key={index}
                      size="sm"
                      colorScheme="gray"
                      aria-label={menu.label}
                      leftIcon={menu.icon ?? <MdMoreVert />}
                      onClick={() => menu.clickHandler(item)}
                      {...{
                        isDisabled: menu.isDisabled
                          ? typeof menu.isDisabled === 'function'
                            ? menu.isDisabled(item)
                            : menu.isDisabled
                          : false,
                      }}
                    >
                      {menu.label}
                    </Button>
                  ),
              )}
            </Flex>
          )}
          {actionsMenuItems.length > 0 && (
            <Flex w="10%" justifyContent="end" pr={4} display={{ base: 'none', lg: 'flex' }}>
              <Menu>
                <MenuButton as={IconButton} aria-label="Actions" icon={<MdMoreVert />} />
                <MenuList>
                  {actionsMenuItems.map(
                    (menu, index) =>
                      ((menu.renderCondition && menu.renderCondition(item)) ||
                        !menu.renderCondition) && (
                        <MenuItem
                          key={index}
                          icon={menu.icon ?? undefined}
                          onClick={() => menu.clickHandler(item)}
                          {...{
                            isDisabled: menu.isDisabled
                              ? typeof menu.isDisabled === 'function'
                                ? menu.isDisabled(item)
                                : menu.isDisabled
                              : false,
                          }}
                        >
                          {menu.label}
                        </MenuItem>
                      ),
                  )}
                </MenuList>
              </Menu>
            </Flex>
          )}
        </Flex>
      ))}
      {usePagination && (
        <Flex direction={'column'} w={'100%'} mt={4}>
          <Pagination
            nextLink={nextLink}
            prevLink={prevLink}
            onNext={handleNextPage}
            onPrev={handlePrevPage}
            pageNumber={pageNumber}
            totalPages={totalPages}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default LomaTable;
